import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import Button from '../../../../../ui/canon/Button';
import Input from '../../../../../ui/canon/Input';
import { SendIcon, PlusIcon, TrashIcon, GripVerticalIcon } from 'lucide-react';

const RepeaterWidget = ({
  onAnswer,
  disabled,
  options = {},
  initialValue = [],
}) => {
  const {
    buttonText = 'Add Item',
    fields = [],
    minItems = 0,
    maxItems = 10,
  } = options;

  const intl = useIntl();
  const [items, setItems] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (Array.isArray(initialValue) && initialValue.length > 0) {
      setItems(initialValue);
    } else if (minItems > 0) {
      // Initialize with minimum required items
      setItems(Array(minItems).fill(null).map(() => createEmptyItem()));
    }
    setIsSubmitting(false);
    setErrors({});
  }, [initialValue]);

  const createEmptyItem = () => {
    const item = {};
    fields.forEach(field => {
      if (field.type === 'checkbox') {
        item[field.id] = [];
      } else {
        item[field.id] = '';
      }
    });
    return item;
  };

  const handleAddItem = () => {
    if (items.length < maxItems) {
      setItems([...items, createEmptyItem()]);
    }
  };

  const handleRemoveItem = (index) => {
    if (items.length > minItems) {
      const newItems = items.filter((_, i) => i !== index);
      setItems(newItems);
      // Clear errors for removed item
      const newErrors = { ...errors };
      delete newErrors[index];
      setErrors(newErrors);
    }
  };

  const handleFieldChange = (itemIndex, fieldId, value) => {
    const newItems = [...items];
    newItems[itemIndex] = { ...newItems[itemIndex], [fieldId]: value };
    setItems(newItems);

    // Clear error for this field
    if (errors[itemIndex]?.[fieldId]) {
      const newErrors = { ...errors };
      delete newErrors[itemIndex][fieldId];
      if (Object.keys(newErrors[itemIndex]).length === 0) {
        delete newErrors[itemIndex];
      }
      setErrors(newErrors);
    }
  };

  const handleCheckboxChange = (itemIndex, fieldId, optionValue, checked) => {
    const newItems = [...items];
    const currentValues = newItems[itemIndex][fieldId] || [];
    if (checked) {
      newItems[itemIndex][fieldId] = [...currentValues, optionValue];
    } else {
      newItems[itemIndex][fieldId] = currentValues.filter(v => v !== optionValue);
    }
    setItems(newItems);
  };

  const validateItems = () => {
    const newErrors = {};
    let hasErrors = false;

    items.forEach((item, itemIndex) => {
      fields.forEach(field => {
        if (field.required) {
          const value = item[field.id];
          const isEmpty = field.type === 'checkbox'
            ? (!value || value.length === 0)
            : (!value || (typeof value === 'string' && !value.trim()));

          if (isEmpty) {
            if (!newErrors[itemIndex]) newErrors[itemIndex] = {};
            newErrors[itemIndex][field.id] = intl.formatMessage({ id: 'succession.plans.widget_repeater_field_required' }, { label: field.label });
            hasErrors = true;
          }
        }
      });
    });

    setErrors(newErrors);
    return !hasErrors;
  };

  const handleSubmit = () => {
    if (!validateItems()) return;

    if (!isSubmitting) {
      setIsSubmitting(true);
      onAnswer({ value: items });
    }
  };

  const renderField = (field, itemIndex) => {
    const item = items[itemIndex];
    const value = item[field.id];
    const error = errors[itemIndex]?.[field.id];
    const fieldKey = `${itemIndex}-${field.id}`;

    switch (field.type) {
      case 'text':
      case 'tel':
      case 'email':
        return (
          <div key={fieldKey} className="space-y-1">
            <label className="text-sm font-medium text-succession-text-primary">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <Input
              type={field.type}
              value={value || ''}
              onChange={(e) => handleFieldChange(itemIndex, field.id, e.target.value)}
              placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
              disabled={disabled || isSubmitting}
            />
            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>
        );

      case 'radio':
        return (
          <div key={fieldKey} className="space-y-2">
            <label className="text-sm font-medium text-succession-text-primary">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="flex flex-wrap gap-3">
              {(field.options || []).map((option) => (
                <label
                  key={`${fieldKey}-${option.value}`}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="radio"
                    name={fieldKey}
                    value={option.value}
                    checked={value === option.value}
                    onChange={(e) => handleFieldChange(itemIndex, field.id, e.target.value)}
                    disabled={disabled || isSubmitting}
                    className="text-succession-primary focus:ring-succession-focus-ring"
                  />
                  <span className="text-sm text-succession-text-secondary">{option.label}</span>
                </label>
              ))}
            </div>
            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>
        );

      case 'checkbox':
        return (
          <div key={fieldKey} className="space-y-2">
            <label className="text-sm font-medium text-succession-text-primary">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="flex flex-wrap gap-3">
              {(field.options || []).map((option) => (
                <label
                  key={`${fieldKey}-${option.value}`}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    value={option.value}
                    checked={(value || []).includes(option.value)}
                    onChange={(e) => handleCheckboxChange(itemIndex, field.id, option.value, e.target.checked)}
                    disabled={disabled || isSubmitting}
                    className="text-succession-primary focus:ring-succession-focus-ring rounded"
                  />
                  <span className="text-sm text-succession-text-secondary">{option.label}</span>
                </label>
              ))}
            </div>
            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>
        );

      default:
        return (
          <div key={fieldKey} className="space-y-1">
            <label className="text-sm font-medium text-succession-text-primary">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <Input
              type="text"
              value={value || ''}
              onChange={(e) => handleFieldChange(itemIndex, field.id, e.target.value)}
              placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
              disabled={disabled || isSubmitting}
            />
            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>
        );
    }
  };

  return (
    <div className="space-y-4">
      {/* Items List */}
      {items.map((item, index) => (
        <div
          key={index}
          className="border border-succession-border rounded-lg p-4 space-y-4 bg-succession-bg-secondary"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <GripVerticalIcon className="h-4 w-4 text-succession-text-muted" />
              <span className="text-sm font-medium text-succession-text-primary">
                {intl.formatMessage({ id: 'succession.plans.widget_repeater_item' }, { number: index + 1 })}
              </span>
            </div>
            {items.length > minItems && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveItem(index)}
                disabled={disabled || isSubmitting}
                className="text-red-500 hover:text-red-600 hover:bg-red-50"
              >
                <TrashIcon className="h-4 w-4" />
              </Button>
            )}
          </div>

          <div className="grid gap-4">
            {fields.map(field => renderField(field, index))}
          </div>
        </div>
      ))}

      {/* Add Item Button */}
      {items.length < maxItems && (
        <Button
          variant="outline"
          onClick={handleAddItem}
          disabled={disabled || isSubmitting}
          className="w-full"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          {buttonText}
        </Button>
      )}

      {/* Submit Button */}
      <Button
        onClick={handleSubmit}
        disabled={items.length === 0 || disabled || isSubmitting}
        className="w-full"
      >
        <SendIcon className="h-4 w-4 mr-2" />
        {intl.formatMessage({ id: 'succession.plans.widget_submit_answer' })}
      </Button>
    </div>
  );
};

export default RepeaterWidget;
